"use client";
import { useState } from "react";
import NextImage from "next/image";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  /** Kept for back-compat — layout is driven by the parent's aspectRatio. */
  width?: number;
  height?: number;
  aspectRatio?: string;
  /** Eager-load and skip the fade-in. Use for above-the-fold images. */
  priority?: boolean;
  /** Responsive sizes hint for srcset. Override per usage when the image
   *  doesn't span the full viewport on mobile (e.g. card grids). */
  sizes?: string;
  /** "cover" (default) fills the box, cropping any excess — fine when the
   *  source is expected to roughly match aspectRatio. Use "contain" when the
   *  source's aspect ratio can vary and nothing should ever be cropped. */
  objectFit?: "cover" | "contain";
  /** Skip next/image's own re-optimization and serve the source bytes as-is.
   *  Use for images that are already correctly sized/encoded server-side —
   *  re-optimizing an already-optimized file is pure downside. */
  unoptimized?: boolean;
}

export function LazyImage({
  src,
  alt,
  className = "",
  style = {},
  aspectRatio = "16/10",
  priority = false,
  sizes = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  objectFit = "cover",
  unoptimized = false,
}: LazyImageProps) {
  // Priority images skip the fade so they're not invisible while waiting on onLoad.
  const [loaded, setLoaded] = useState(priority);

  return (
    <div
      style={{
        position: "relative",
        aspectRatio,
        background: "#f2f4f7",
        overflow: "hidden",
        ...style,
      }}
      className={className}
    >
      {/* Shimmer placeholder */}
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, #f2f4f7 25%, #e5e7eb 50%, #f2f4f7 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s ease-in-out infinite",
          }}
        />
      )}

      <NextImage
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        priority={priority}
        unoptimized={unoptimized}
        onLoad={() => setLoaded(true)}
        style={{
          objectFit,
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
      />

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
